// API client configuration
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Helper function for making authenticated API requests
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  token?: string | null
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  // Add auth token if provided
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || error.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

// Auth API
export const authApi = {
  getMe: (token: string) =>
    apiRequest('/auth/me', {}, token),
  updateProfile: (data: { firstName?: string; lastName?: string }, token: string) =>
    apiRequest('/auth/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }, token),
};

// Jobs API
export const jobsApi = {
  getJobs: (params?: Record<string, any>, token?: string) =>
    apiRequest(`/jobs${params ? '?' + new URLSearchParams(params).toString() : ''}`, {}, token),
  getJob: (id: string, token?: string) =>
    apiRequest(`/jobs/${id}`, {}, token),
  getFeaturedJobs: (limit?: number) =>
    apiRequest(`/jobs/featured${limit ? `?limit=${limit}` : ''}`),
  submitJob: (data: any, token: string) =>
    apiRequest('/jobs/submit', {
      method: 'POST',
      body: JSON.stringify(data),
    }, token),
  getMySubmissions: (token: string) =>
    apiRequest('/jobs/submissions/mine', {}, token),
};

// Companies API
export const companiesApi = {
  getCompanies: (params?: Record<string, any>) =>
    apiRequest(`/companies${params ? '?' + new URLSearchParams(params).toString() : ''}`),
  getCompany: (slug: string, token?: string) =>
    apiRequest(`/companies/${slug}`, {}, token),
  getCompanyReviews: (id: string, params?: Record<string, any>, token?: string) =>
    apiRequest(`/companies/${id}/reviews${params ? '?' + new URLSearchParams(params).toString() : ''}`, {}, token),
  submitReview: (id: string, data: any, token: string) =>
    apiRequest(`/companies/${id}/reviews`, {
      method: 'POST',
      body: JSON.stringify(data),
    }, token),
};
